import { useState } from "react"
import {
  Users, Link2, MessageCircle, Star, TrendingUp,
  ArrowRight, Activity, Clock, Filter, RefreshCw,
  Send,
  Linkedin,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  useOutreachMetrics,
  useConversionFunnel,
  useOutreachEvents,
  useOutreachQueue,
} from "@/hooks/useOutreach"
import { useLeadStats } from "@/hooks/useLeads"
import { OUTREACH_STATUS_CONFIG, EVENT_TYPE_CONFIG } from "@/types/outreach"
import type { OutreachStatus, OutreachEventType, OutreachEvent } from "@/types/outreach"

// ── Period Selector ──
const PERIODS = [
  { label: "7 dias", value: 7 },
  { label: "30 dias", value: 30 },
  { label: "90 dias", value: 90 },
  { label: "Tudo", value: 365 },
]

// ── KPI Card ──
function KPICard({
  icon: Icon,
  label,
  value,
  color,
  rate,
  rateLabel,
}: {
  icon: React.ElementType
  label: string
  value: number | string
  color: string
  rate?: number | null
  rateLabel?: string
}) {
  return (
    <div className="bg-card border rounded-xl p-5 flex flex-col gap-2 relative overflow-hidden group hover:border-primary/30 transition-colors">
      <div className="absolute top-0 right-0 w-20 h-20 rounded-bl-full opacity-10" style={{ background: color }} />
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <span className="text-3xl font-bold tracking-tight">{value}</span>
      {rate != null && (
        <div className="flex items-center gap-1.5 text-xs">
          <ArrowRight className="w-3 h-3 text-muted-foreground" />
          <span className="font-semibold" style={{ color }}>
            {typeof rate === "number" ? `${rate.toFixed(1)}%` : rate}
          </span>
          {rateLabel && <span className="text-muted-foreground">{rateLabel}</span>}
        </div>
      )}
    </div>
  )
}

// ── Funnel Bar ──
function FunnelStage({
  label,
  count,
  maxCount,
  color,
  conversionRate,
  isLast,
}: {
  label: string
  count: number
  maxCount: number
  color: string
  conversionRate?: number
  isLast?: boolean
}) {
  const width = maxCount > 0 ? Math.max((count / maxCount) * 100, 8) : 8
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <span className="text-sm font-bold">{count}</span>
        </div>
        <div className="h-8 bg-muted/30 rounded-md overflow-hidden relative">
          <div
            className="h-full rounded-md transition-all duration-700 ease-out flex items-center justify-end pr-3"
            style={{ width: `${width}%`, background: `linear-gradient(90deg, ${color}cc, ${color})` }}
          >
            {count > 0 && <span className="text-xs font-bold text-white drop-shadow">{count}</span>}
          </div>
        </div>
      </div>
      {!isLast && (
        <div className="flex flex-col items-center w-16 flex-shrink-0">
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          {conversionRate != null && (
            <span className="text-[10px] font-semibold text-muted-foreground">{conversionRate.toFixed(1)}%</span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Donut Chart (CSS conic-gradient) ──
function DonutChart({ data, total }: { data: { label: string; value: number; color: string }[]; total: number }) {
  // Build conic gradient
  let cumulative = 0
  const stops = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const start = cumulative
      cumulative += (d.value / total) * 100
      return `${d.color} ${start}% ${cumulative}%`
    })
    .join(", ")

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-40 h-40 flex-shrink-0">
        <div
          className="w-full h-full rounded-full"
          style={{
            background: total > 0 ? `conic-gradient(${stops})` : "#334155",
          }}
        />
        <div className="absolute inset-4 rounded-full bg-card flex items-center justify-center">
          <div className="text-center">
            <span className="text-2xl font-bold">{total}</span>
            <br />
            <span className="text-[10px] text-muted-foreground">TOTAL</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 overflow-hidden">
        {data
          .filter((d) => d.value > 0)
          .map((d) => (
            <div key={d.label} className="flex items-center gap-2 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: d.color }} />
              <span className="text-muted-foreground truncate">{d.label}</span>
              <span className="font-bold ml-auto">{d.value}</span>
              <span className="text-muted-foreground w-10 text-right">
                {total > 0 ? `${((d.value / total) * 100).toFixed(0)}%` : "0%"}
              </span>
            </div>
          ))}
      </div>
    </div>
  )
}

// ── Activity Timeline Item ──
function TimelineItem({ event }: { event: OutreachEvent }) {
  const cfg = EVENT_TYPE_CONFIG[event.event_type as OutreachEventType]
  const Icon = cfg?.icon || Activity
  const color = cfg?.color || "#6b7280"
  const title = cfg?.title || event.event_type
  const time = new Date(event.created_at).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })

  return (
    <div className="flex gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium truncate">{title}</span>
          <span className="text-[10px] text-muted-foreground flex-shrink-0">{time}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {event.contact_name && <span className="font-medium text-foreground">{event.contact_name}</span>}
          {event.lead_company && <span> — {event.lead_company}</span>}
          {!event.contact_name && !event.lead_company && event.subject && <span>{event.subject}</span>}
        </div>
      </div>
    </div>
  )
}

// ── Metric Row (for Activity Metrics section) ──
function MetricRow({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-bold" style={color ? { color } : {}}>
        {value}
      </span>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════

export default function MQLDashboardPage() {
  const [days, setDays] = useState(90)

  const { data: metrics, isLoading: metricsLoading } = useOutreachMetrics(days)
  const { data: funnel, isLoading: funnelLoading } = useConversionFunnel()
  const { data: leadStats, isLoading: statsLoading } = useLeadStats()
  const { data: events, isLoading: eventsLoading } = useOutreachEvents({ limit: 12 })
  const { data: queue } = useOutreachQueue(5)

  const totalLeads = leadStats?.total || 0
  const byStatus = leadStats?.by_status || {}

  // Outreach status counts from funnel
  const funnelData = funnel || {
    invite_sent: 0,
    connected: 0,
    msg_sent: 0,
    replied: 0,
    qualified: 0,
    opportunity: 0,
    conversion_rates: {},
  }

  // Calculate contacted: everything except not_contacted
  const contacted =
    funnelData.invite_sent +
    funnelData.connected +
    funnelData.msg_sent +
    funnelData.replied +
    funnelData.qualified +
    funnelData.opportunity

  // Donut data for outreach status distribution
  const outreachDonutData = [
    { label: "Nao Contatado", value: Math.max(totalLeads - contacted, 0), color: "#94a3b8" },
    { label: "Convite Enviado", value: funnelData.invite_sent, color: "#3b82f6" },
    { label: "Conectado", value: funnelData.connected, color: "#22c55e" },
    { label: "Msg Enviada", value: funnelData.msg_sent, color: "#8b5cf6" },
    { label: "Respondeu", value: funnelData.replied, color: "#f59e0b" },
    { label: "Qualificado (MQL)", value: funnelData.qualified, color: "#10b981" },
    { label: "Oportunidade", value: funnelData.opportunity, color: "#f97316" },
  ]

  // Lead status donut
  const leadStatusDonut = [
    { label: "Suspect", value: byStatus.SUSPECT || 0, color: "#94a3b8" },
    { label: "Prospect", value: byStatus.PROSPECT || 0, color: "#3b82f6" },
    { label: "MQL", value: byStatus.MQL || 0, color: "#10b981" },
    { label: "SQL", value: byStatus.SQL || 0, color: "#f59e0b" },
    { label: "Opportunity", value: byStatus.OPPORTUNITY || 0, color: "#f97316" },
    { label: "Customer", value: byStatus.CUSTOMER || 0, color: "#22c55e" },
    { label: "Lost", value: byStatus.LOST || 0, color: "#ef4444" },
    { label: "Disqualified", value: byStatus.DISQUALIFIED || 0, color: "#6b7280" },
  ]

  // Funnel stages
  const funnelStages = [
    { label: "Nao Contatados", count: Math.max(totalLeads - contacted, 0), color: "#94a3b8" },
    { label: "Convite Enviado", count: funnelData.invite_sent, color: "#3b82f6" },
    { label: "Conectados", count: funnelData.connected, color: "#22c55e" },
    { label: "Msg Enviada", count: funnelData.msg_sent, color: "#8b5cf6" },
    { label: "Responderam", count: funnelData.replied, color: "#f59e0b" },
    { label: "Qualificados (MQL)", count: funnelData.qualified, color: "#10b981" },
    { label: "Oportunidades", count: funnelData.opportunity, color: "#f97316" },
  ]

  const maxFunnelCount = Math.max(...funnelStages.map((s) => s.count), 1)

  const isLoading = metricsLoading || funnelLoading || statsLoading

  return (
    <div className="space-y-6 p-1">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">MQL Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visao consolidada do funil de qualificacao via LinkedIn Outreach
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <div className="flex bg-muted rounded-lg p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setDays(p.value)}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                  days === p.value
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Loading State ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-3 text-sm text-muted-foreground">Carregando metricas...</span>
        </div>
      )}

      {!isLoading && (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KPICard
              icon={Users}
              label="Total Leads"
              value={totalLeads}
              color="#3b82f6"
            />
            <KPICard
              icon={Send}
              label="Contatados"
              value={contacted}
              color="#22c55e"
              rate={totalLeads > 0 ? (contacted / totalLeads) * 100 : 0}
              rateLabel="do total"
            />
            <KPICard
              icon={Link2}
              label="Conectados"
              value={funnelData.connected}
              color="#06b6d4"
              rate={metrics?.acceptance_rate}
              rateLabel="taxa aceite"
            />
            <KPICard
              icon={MessageCircle}
              label="Responderam"
              value={funnelData.replied}
              color="#8b5cf6"
              rate={metrics?.reply_rate}
              rateLabel="taxa resposta"
            />
            <KPICard
              icon={Star}
              label="MQLs"
              value={funnelData.qualified}
              color="#eab308"
              rate={contacted > 0 ? (funnelData.qualified / contacted) * 100 : 0}
              rateLabel="dos contatados"
            />
            <KPICard
              icon={TrendingUp}
              label="Oportunidades"
              value={funnelData.opportunity}
              color="#f97316"
              rate={funnelData.qualified > 0 ? (funnelData.opportunity / funnelData.qualified) * 100 : 0}
              rateLabel="dos MQLs"
            />
          </div>

          {/* ── Funnel Visualization ── */}
          <div className="bg-card border rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Funil de Conversao LinkedIn Outreach
            </h2>
            <div className="space-y-2">
              {funnelStages.map((stage, i) => {
                const next = funnelStages[i + 1]
                const convRate = next && stage.count > 0 ? (next.count / stage.count) * 100 : undefined
                return (
                  <FunnelStage
                    key={stage.label}
                    label={stage.label}
                    count={stage.count}
                    maxCount={maxFunnelCount}
                    color={stage.color}
                    conversionRate={convRate}
                    isLast={i === funnelStages.length - 1}
                  />
                )
              })}
            </div>
          </div>

          {/* ── Middle Row: Activity Metrics + Donut Charts ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Activity Metrics */}
            <div className="bg-card border rounded-xl p-6 space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Send className="w-4 h-4" /> Envios
              </h3>
              <MetricRow label="Convites enviados" value={metrics?.invites_sent || 0} />
              <MetricRow label="Mensagens enviadas" value={metrics?.messages_sent || 0} />
              <MetricRow label="Perfis visualizados" value={metrics?.profiles_viewed || 0} />
              <MetricRow label="Leads criados" value={metrics?.leads_created || 0} color="#22c55e" />

              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 pt-2">
                <MessageCircle className="w-4 h-4" /> Respostas
              </h3>
              <MetricRow label="Convites aceitos" value={metrics?.invites_accepted || 0} />
              <MetricRow label="Mensagens recebidas" value={metrics?.messages_received || 0} />
              <MetricRow
                label="Taxa de aceitacao"
                value={`${(metrics?.acceptance_rate || 0).toFixed(1)}%`}
                color="#06b6d4"
              />
              <MetricRow
                label="Taxa de resposta"
                value={`${(metrics?.reply_rate || 0).toFixed(1)}%`}
                color="#8b5cf6"
              />

              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 pt-2">
                <TrendingUp className="w-4 h-4" /> Conversao
              </h3>
              <MetricRow label="Oportunidades geradas" value={metrics?.opps_created || 0} color="#f97316" />
              <MetricRow
                label="Score medio dos leads"
                value={(leadStats?.avg_score || 0).toFixed(1)}
              />
              <MetricRow
                label="Convertidos este mes"
                value={leadStats?.converted_this_month || 0}
                color="#22c55e"
              />
            </div>

            {/* Outreach Status Donut */}
            <div className="bg-card border rounded-xl p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Distribuicao por Status Outreach
              </h3>
              <DonutChart data={outreachDonutData} total={totalLeads} />
            </div>

            {/* Lead Status Donut */}
            <div className="bg-card border rounded-xl p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Distribuicao por Status do Lead
              </h3>
              <DonutChart data={leadStatusDonut} total={totalLeads} />
            </div>
          </div>

          {/* ── Bottom Row: Activity Feed + Queue ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Activity Feed */}
            <div className="bg-card border rounded-xl p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Atividade Recente
              </h3>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : events?.items && events.items.length > 0 ? (
                <div className="max-h-[400px] overflow-y-auto pr-1">
                  {events.items.map((event) => (
                    <TimelineItem key={event.id} event={event} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Nenhuma atividade recente
                </div>
              )}
            </div>

            {/* Pending Actions Queue */}
            <div className="bg-card border rounded-xl p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" /> Proximas Acoes
              </h3>
              {queue?.items && queue.items.length > 0 ? (
                <div className="space-y-3">
                  {queue.items.map((item) => {
                    const statusCfg = OUTREACH_STATUS_CONFIG[item.outreach_status as OutreachStatus]
                    const StatusIcon = statusCfg?.icon
                    return (
                      <div
                        key={item.lead_id}
                        className="flex items-center gap-3 py-2.5 px-3 bg-muted/30 rounded-lg"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: `${statusCfg?.color || "#6b7280"}20` }}
                        >
                          {StatusIcon && <StatusIcon className="w-4 h-4" style={{ color: statusCfg?.color }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {item.contact_name || item.company_name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{item.next_action}</div>
                        </div>
                        {item.days_overdue > 0 && (
                          <span className="text-[10px] font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full">
                            {item.days_overdue}d atrasado
                          </span>
                        )}
                      </div>
                    )
                  })}
                  {queue.overdue > 0 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-red-500 font-medium">
                        {queue.overdue} acoes atrasadas
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Nenhuma acao pendente
                </div>
              )}
            </div>
          </div>

          {/* ── Summary Footer ── */}
          <div className="bg-card border rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Linkedin className="w-4 h-4 text-[#0A66C2]" />
              <span>
                Periodo: {metrics?.period_start ? new Date(metrics.period_start).toLocaleDateString("pt-BR") : "—"} a{" "}
                {metrics?.period_end ? new Date(metrics.period_end).toLocaleDateString("pt-BR") : "—"}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span>Total eventos: {events?.total || 0}</span>
              <span>|</span>
              <span>Fila pendente: {queue?.total || 0}</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
